import React from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send, Clock, Building2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import ParticleBackground from '../components/ParticleBackground';

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    gdprConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.gdprConsent) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      emailjs.init('saGOmdFFdZT_Ravmd');

      const templateParams = {
        from_name: formData.name,
        reply_to: formData.email,
        phone: formData.phone || 'Neuvedeno',
        message: formData.message,
        email: 'kontakt@digitalshrooms.cz'
      };

      await emailjs.send(
        'service_vxop1mt',
        'template_rzipdon',
        templateParams
      );

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        gdprConsent: false
      });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Nadpis + úvod */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('contact.subtitle')} <br /><br />
              <span className="text-red-400 font-semibold">{t('contact.serviceNote')}</span>
            </p>
          </motion.div>

          {/* Dvě sloupce: vlevo info + mapa, vpravo formulář */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch order-reverse lg:order-none">
            {/* Levý sloupec */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col space-y-8 order-2 lg:order-1"
            >
              {/* Kontaktní informace */}
              <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6 font-orbitron">
                  {t('contact.contactInfo')}
                </h2>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#EE1C23]/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-[#EE1C23]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Email</p>
                      <p className="text-white font-medium">cybertronic@email.cz</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#EE1C23]/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#EE1C23]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">{t('contact.phone')}</p>
                      <p className="text-white font-medium">+420 777 875 098 (WhatsApp)</p>
                      <p className="text-gray-400 text-xs">{t('contact.phoneNote')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#EE1C23]/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#EE1C23]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">{t('contact.workingHours')}</p>
                      {t('contact.workingHoursText').split('\n').map((line, index) => (
                        <p key={index} className="text-white font-medium">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Adresy */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                    <Building2 className="w-5 h-5 text-[#EE1C23]" />
                    <span>{t('contact.billingAddress')}</span>
                  </h3>
                  <p className="text-gray-300 text-sm">
                    {t('contact.billingAddressText').split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < t('contact.billingAddressText').split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                    <MapPin className="w-5 h-5 text-[#EE1C23]" />
                    <span>{t('contact.contactAddress')}</span>
                  </h3>
                  <p className="text-gray-300 text-sm">
                    {t('contact.contactAddressText').split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < t('contact.contactAddressText').split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </div>

              {/* Firemní údaje collapsible */}
              <details className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-6 text-gray-300">
                <summary className="cursor-pointer text-white font-semibold">{t('contact.companyInfo')}</summary>
                <div className="mt-4 space-y-2 text-sm">
                  {t('contact.companyInfoText').split('\n').map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </details>

              {/* Mapa */}
              <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-8 shadow-lg flex-1 flex flex-col">
                <h2 className="text-2xl font-bold text-white mb-4 font-orbitron">
                  {t('contact.map')}
                </h2>
                <div className="overflow-hidden rounded-xl h-72 mb-4 flex-1">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2618.121543172322!2d17.090336876852195!3d48.98924439148857!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471321cc132fe05b%3A0x3961533d4e2b8b3d!2sCYBERTRONIC%20Robotics%20s.r.o.!5e0!3m2!1scs!2scz!4v1755628893369!5m2!1scs!2scz"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </motion.div>

            {/* Pravý sloupec - formulář */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex order-1 lg:order-2"
            >
              <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-8 flex-1">
                <h2 className="text-2xl font-bold text-white mb-6 font-orbitron">
                  {t('contact.contactUs')}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Jméno */}
                  <div>
                    <label className="block text-gray-300 mb-2">
                      {t('contact.name')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors"
                      placeholder={t('contact.namePlaceholder')}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-gray-300 mb-2">
                      {t('contact.email')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors"
                      placeholder={t('contact.emailPlaceholder')}
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="block text-gray-300 mb-2">
                      {t('contact.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors"
                      placeholder={t('contact.phonePlaceholder')}
                    />
                  </div>

                  {/* Zpráva */}
                  <div>
                    <label className="block text-gray-300 mb-2">
                      {t('contact.message')} <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none transition-colors resize-none"
                      placeholder={t('contact.messagePlaceholder')}
                    ></textarea>
                  </div>

                  {/* GDPR */}
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="gdprConsent"
                      checked={formData.gdprConsent}
                      onChange={handleChange}
                      required
                      className="mt-1 w-4 h-4 text-[#EE1C23] bg-gray-800 border-gray-600 rounded focus:ring-[#EE1C23] focus:ring-2"
                    />
                    <label className="text-gray-300 text-sm">
                      {t('contact.gdpr')} <span className="text-red-400">*</span>
                    </label>
                  </div>

                  {/* Submit */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={!formData.gdprConsent || isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    <span>
                      {isSubmitting ? t('contact.sending') : t('contact.send')}
                    </span>
                  </motion.button>

                  {/* Status messages */}
                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-600/20 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm text-center">
                        ✅ {t('contact.successMessage')}
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-600/20 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm text-center">
                        ❌ {t('contact.errorMessage')}
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
